import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Container, Row, Col, Card, Table, Button, Tabs, Tab, Badge } from 'react-bootstrap';
import "bootstrap/dist/css/bootstrap.min.css";
import { getWhatsappJourneys } from '../../actions/whatsappAnalytics';
import DateRangeBar, { useDateRange } from './DateRangeBar';
import FunnelBar from './FunnelBar';
import { LoadingState, ErrorBanner } from './StateViews';
import { JOURNEY_TABS, humanize, stageLabel } from './labels';

const NO_VALUE = '-';
const MESSAGES_PATH = '/inventory/v1.2/whatsapp/messages';

const formatCount = (value) => (Number.isFinite(value) ? value.toLocaleString('en-IN') : NO_VALUE);

const messageLogLink = (journey, stage, range) => {
    const params = new URLSearchParams({ journey, stage, from: range.from, to: range.to });
    return `${MESSAGES_PATH}?${params.toString()}`;
};

const ChoiceList = ({ choices }) => {
    if (choices.length === 0) return <div className="text-muted small">No taps in this range.</div>;

    const total = choices.reduce((sum, { count }) => sum + count, 0);

    return (
        <Table size="sm" borderless className="mb-0">
            <thead>
                <tr className="text-muted small">
                    <th>Choice</th>
                    <th className="text-end">Taps</th>
                    <th style={{ width: '40%' }}>Share of taps</th>
                </tr>
            </thead>
            <tbody>
                {choices.map(({ choice, label, count }) => (
                    <tr key={choice}>
                        <td>{label}</td>
                        <td className="text-end">{formatCount(count)}</td>
                        <td>
                            <FunnelBar
                                percent={total > 0 ? Math.round((count / total) * 1000) / 10 : null}
                                variant="info"
                                label={`${label} share of taps`}
                            />
                        </td>
                    </tr>
                ))}
            </tbody>
        </Table>
    );
};

ChoiceList.propTypes = {
    choices: PropTypes.arrayOf(PropTypes.shape({
        choice: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        count: PropTypes.number.isRequired
    })).isRequired
};

const StageRows = ({ journey, stage, range, expanded, onToggle }) => {
    const hasChoices = stage.choices.length > 0;

    return (
        <>
            <tr>
                <td>
                    <div className="fw-semibold">{stageLabel(stage.stage)}</div>
                    <div className="text-muted small">{stage.template || 'No template, customer starts it'}</div>
                </td>
                <td className="text-end">
                    {stage.sent === null ? NO_VALUE : (
                        <Link to={messageLogLink(journey, stage.stage, range)} title="Open these messages in the Message Log">
                            {formatCount(stage.sent)}
                        </Link>
                    )}
                </td>
                <td className="text-end">{formatCount(stage.tapped)}</td>
                <td><FunnelBar percent={stage.tappedPercent} label={`${stageLabel(stage.stage)} tapped of sent`} /></td>
                <td className="text-end">{stage.converted === null ? NO_VALUE : formatCount(stage.converted)}</td>
                <td><FunnelBar percent={stage.convertedPercent} variant="success" label={`${stageLabel(stage.stage)} converted of tapped`} /></td>
                <td className="text-end">
                    <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={!hasChoices}
                        aria-expanded={expanded}
                        onClick={() => onToggle(stage.stage)}
                    >
                        {expanded ? 'Hide' : 'Choices'}
                        {hasChoices && <Badge bg="secondary" className="ms-1">{stage.choices.length}</Badge>}
                    </Button>
                </td>
            </tr>
            {expanded && hasChoices && (
                <tr className="bg-light">
                    <td colSpan={7}>
                        <ChoiceList choices={stage.choices} />
                    </td>
                </tr>
            )}
        </>
    );
};

StageRows.propTypes = {
    journey: PropTypes.string.isRequired,
    stage: PropTypes.object.isRequired,
    range: PropTypes.shape({ from: PropTypes.string.isRequired, to: PropTypes.string.isRequired }).isRequired,
    expanded: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired
};

const ExitReasons = ({ exits }) => {
    const rows = Object.entries(exits).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    const total = rows.reduce((sum, [, count]) => sum + count, 0);

    return (
        <Card className="shadow-sm mt-3">
            <Card.Header className="bg-white fw-semibold">Exit reasons</Card.Header>
            <Card.Body className="p-0">
                {total === 0 ? (
                    <div className="text-muted p-3">No enrollments exited in this range.</div>
                ) : (
                    <Table responsive hover className="mb-0">
                        <thead>
                            <tr>
                                <th>Reason</th>
                                <th className="text-end">Enrollments</th>
                                <th style={{ width: '40%' }}>Share of exits</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(([reason, count]) => (
                                <tr key={reason}>
                                    <td>{humanize(reason)}</td>
                                    <td className="text-end">{formatCount(count)}</td>
                                    <td><FunnelBar percent={Math.round((count / total) * 1000) / 10} variant="warning" label={`${humanize(reason)} share of exits`} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Card.Body>
        </Card>
    );
};

ExitReasons.propTypes = {
    exits: PropTypes.objectOf(PropTypes.number).isRequired
};

const JourneyPanel = ({ journey, range }) => {
    const [expandedStage, setExpandedStage] = useState(null);

    const toggle = useCallback((stage) => {
        setExpandedStage((current) => (current === stage ? null : stage));
    }, []);

    return (
        <>
            <Card className="shadow-sm">
                <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                    <span className="fw-semibold">Stages</span>
                    <span className="text-muted small">Enrolled in range: {formatCount(journey.enrolled)}</span>
                </Card.Header>
                <Card.Body className="p-0">
                    <Table responsive hover className="mb-0 align-middle">
                        <thead>
                            <tr>
                                <th>Stage</th>
                                <th className="text-end">Sent</th>
                                <th className="text-end">Tapped</th>
                                <th>Tapped of sent</th>
                                <th className="text-end">Converted</th>
                                <th>Converted of tapped</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {journey.stages.map((stage) => (
                                <StageRows
                                    key={stage.stage}
                                    journey={journey.journey}
                                    stage={stage}
                                    range={range}
                                    expanded={expandedStage === stage.stage}
                                    onToggle={toggle}
                                />
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
            <ExitReasons exits={journey.exits} />
        </>
    );
};

JourneyPanel.propTypes = {
    journey: PropTypes.object.isRequired,
    range: PropTypes.shape({ from: PropTypes.string.isRequired, to: PropTypes.string.isRequired }).isRequired
};

const JourneyFunnels = ({ journeys, error, getWhatsappJourneys }) => {
    const { range, setRange } = useDateRange();
    const [activeTab, setActiveTab] = useState(JOURNEY_TABS[0].key);

    const load = useCallback(() => {
        getWhatsappJourneys({ from: range.from, to: range.to });
    }, [range.from, range.to, getWhatsappJourneys]);

    useEffect(() => {
        load();
    }, [load]);

    const ready = Boolean(journeys && journeys.range.from === range.from && journeys.range.to === range.to);

    const renderBody = () => {
        if (!ready) {
            return error ? <ErrorBanner message={error} onRetry={load} /> : <LoadingState />;
        }

        const byKey = Object.fromEntries(journeys.journeys.map((journey) => [journey.journey, journey]));

        return (
            <Tabs activeKey={activeTab} onSelect={(key) => key && setActiveTab(key)} className="mb-3" mountOnEnter unmountOnExit>
                {JOURNEY_TABS.filter(({ key }) => byKey[key]).map(({ key, title }) => (
                    <Tab key={key} eventKey={key} title={title}>
                        <JourneyPanel journey={byKey[key]} range={range} />
                    </Tab>
                ))}
            </Tabs>
        );
    };

    return (
        <Container fluid className="py-4">
            <Row className="mb-4 align-items-center g-3">
                <Col xs={12} lg>
                    <h2 className="mb-1">WhatsApp Journeys</h2>
                    <p className="text-muted mb-0">
                        Days are IST. Each stage counts customers sent that stage in the range. Tapped is of sent, converted is of tapped.
                    </p>
                </Col>
                <Col xs={12} lg="auto">
                    <DateRangeBar range={range} onChange={setRange} />
                </Col>
            </Row>
            {renderBody()}
        </Container>
    );
};

JourneyFunnels.propTypes = {
    journeys: PropTypes.object,
    error: PropTypes.string,
    getWhatsappJourneys: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
    journeys: state.whatsappAnalytics.journeys,
    error: state.whatsappAnalytics.error
});

export default connect(mapStateToProps, { getWhatsappJourneys })(JourneyFunnels);
